// ============================================================================
// PATIENT NOTES BLOCK
// Bloque colapsable de notas internas del paciente (por centro).
// ============================================================================
import { useState, useEffect, useCallback } from 'react';
import { StickyNote, Plus, Pin, Edit2, Trash2, History, PinOff } from 'lucide-react';
import { toast } from '../ui/Toast';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { patientNotesApi } from '../../api/patient-notes';
import type { PatientNote } from '../../types/patient';
import { NoteEditorModal } from './NoteEditorModal';
import { NoteEditHistoryModal } from './NoteEditHistoryModal';

interface Props {
  patientId: string;
  userId?: string;
  onChanged?: () => void;     // callback para refrescar timeline u otros bloques tras cambios
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function getInitials(name: string | null): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function PatientNotesBlock({ patientId, userId, onChanged }: Props) {
  const [notes, setNotes] = useState<PatientNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<PatientNote | null>(null);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyNoteId, setHistoryNoteId] = useState<string | null>(null);

  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; note: PatientNote | null }>({
    isOpen: false, note: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await patientNotesApi.getByPatient(patientId);
      setNotes(result);
    } catch (err: any) {
      setError(err?.message || 'Error al cargar las notas');
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const handleNewNote = () => {
    setEditingNote(null);
    setEditorOpen(true);
  };

  const handleEdit = (note: PatientNote) => {
    setEditingNote(note);
    setEditorOpen(true);
  };

  const handleViewHistory = (note: PatientNote) => {
    setHistoryNoteId(note.id);
    setHistoryOpen(true);
  };

  const handleTogglePin = async (note: PatientNote) => {
    try {
      await patientNotesApi.togglePin(note.id, !note.pinned);
      toast.success(note.pinned ? 'Nota desfijada' : 'Nota fijada');
      await load();
      onChanged?.();
    } catch (err: any) {
      toast.error('Error', err?.message || 'No se pudo cambiar el estado');
    }
  };

  const handleDelete = (note: PatientNote) => {
    setDeleteDialog({ isOpen: true, note });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.note) return;
    setIsDeleting(true);
    try {
      await patientNotesApi.remove(deleteDialog.note.id);
      toast.success('Nota eliminada');
      setDeleteDialog({ isOpen: false, note: null });
      await load();
      onChanged?.();
    } catch (err: any) {
      toast.error('Error', err?.message || 'No se pudo eliminar la nota');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaved = async () => {
    await load();
    onChanged?.();
  };

  const pinnedCount = notes.filter((n) => n.pinned).length;

  return (
    <>
      <CollapsibleSection
        id="notes"
        title="Notas internas"
        icon={<StickyNote className="h-5 w-5 text-gray-400" />}
        badge={
          <span className="text-sm font-normal text-gray-500">
            {notes.length} {notes.length === 1 ? 'nota' : 'notas'}
            {pinnedCount > 0 && <span className="text-amber-600"> · {pinnedCount} fijada{pinnedCount > 1 ? 's' : ''}</span>}
          </span>
        }
        defaultOpen={true}
        userId={userId}
        actions={
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleNewNote(); }}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-[#008DD0] text-white rounded-lg hover:bg-[#0077b0] transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nueva nota
          </button>
        }
      >
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm mb-3">
            {error}
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-gray-400">Cargando...</p>
        ) : notes.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-4 text-center">
            No hay notas para este paciente en este centro. Haz clic en "Nueva nota" para añadir la primera.
          </p>
        ) : (
          <ul className="space-y-3">
            {notes.map((note) => (
              <li
                key={note.id}
                className={`rounded-lg border p-3 transition-colors ${
                  note.pinned
                    ? 'bg-amber-50/50 border-amber-200'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Cabecera de la nota */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                    {getInitials(note.createdByName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1 flex-wrap">
                      <span className="font-medium text-gray-700">
                        {note.createdByName || 'Usuario desconocido'}
                      </span>
                      <span>·</span>
                      <span>{formatDateTime(note.createdAt)}</span>
                      {note.pinned && (
                        <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 text-[10px]">
                          <Pin className="h-3 w-3" />
                          Fijada
                        </span>
                      )}
                      {note.editCount > 0 && (
                        <button
                          onClick={() => handleViewHistory(note)}
                          className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 text-[10px] hover:bg-gray-200"
                          title={`Editada ${note.editCount} ${note.editCount === 1 ? 'vez' : 'veces'}. Clic para ver histórico.`}
                        >
                          <History className="h-3 w-3" />
                          Editada {note.editCount === 1 ? '1 vez' : `${note.editCount} veces`}
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                      {note.content}
                    </p>
                  </div>

                  {/* Acciones */}
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => handleTogglePin(note)}
                      className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                      title={note.pinned ? 'Desfijar' : 'Fijar'}
                    >
                      {note.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                    </button>
                    {note.canEdit && (
                      <button
                        onClick={() => handleEdit(note)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Editar"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    )}
                    {note.canDelete && (
                      <button
                        onClick={() => handleDelete(note)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CollapsibleSection>

      {/* Modales */}
      <NoteEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={handleSaved}
        patientId={patientId}
        note={editingNote}
      />

      <NoteEditHistoryModal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        noteId={historyNoteId}
      />

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => !isDeleting && setDeleteDialog({ isOpen: false, note: null })}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        variant="warning"
        title="¿Eliminar esta nota?"
        message="La nota se eliminará del listado, pero seguirá apareciendo en el timeline como evento histórico."
        confirmText="Eliminar"
      />
    </>
  );
}

export default PatientNotesBlock;
